import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { trigger, style, transition, animate } from '@angular/animations';

const DISMISS_KEY_PREFIX = 'announcement.dismissed.';
const CSS_VAR_BAR_HEIGHT = '--notification-bar-height';

@Component({
  selector: 'app-notification-bar',
  templateUrl: './notification-bar.component.html',
  styleUrls: ['./notification-bar.component.scss'],
  animations: [trigger('slideFade', [transition(':leave', [animate('0.2s linear', style({ height: 0, opacity: 0 }))])])]
})
export class NotificationBarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() type: 'info' | 'warning' | 'success' | 'danger' = 'info';
  @Input() closeable = true;
  @Input() persistKey?: string;
  @Input() icon?: string;
  @Input() href?: string;
  @Input() target?: string;

  @ViewChild('bar', { static: false }) barRef?: ElementRef<HTMLElement>;

  isVisible = true;
  private resizeObserver?: ResizeObserver;

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2) {}

  ngOnInit() {
    if (this.persistKey && this.isDismissed()) {
      this.isVisible = false;
    }
  }

  ngAfterViewInit() {
    if (!this.isVisible) {
      return;
    }
    this.observeBarHeight();
  }

  ngOnDestroy() {
    this.disconnectObserver();
    this.clearBarHeight();
  }

  close(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isVisible = false;
    if (this.persistKey) {
      try {
        localStorage.setItem(DISMISS_KEY_PREFIX + this.persistKey, 'true');
      } catch (_) {
        // localStorage 不可（プライベートモード等）の場合は無視
      }
    }
    this.disconnectObserver();
    this.clearBarHeight();
  }

  getIconName(): string {
    if (this.icon) {
      return this.icon;
    }
    switch (this.type) {
      case 'success':
        return 'check_circle';
      case 'warning':
      case 'danger':
        return 'warning';
      default:
        return 'info';
    }
  }

  isAnchorHref(): boolean {
    return !!this.href && this.href.startsWith('#');
  }

  isNewTab(): boolean {
    return this.target === '_blank';
  }

  onAnchorClick(event: Event) {
    // href 未指定（リンク無し）: 既定動作を抑止するだけで後続処理はしない
    if (!this.href) {
      event.preventDefault();
      return;
    }
    // 他ページ/外部 URL: ブラウザ既定のリンク遷移に委ねる
    if (!this.isAnchorHref()) {
      return;
    }
    // ページ内アンカー: 既定動作を抑止してスムーズスクロール
    event.preventDefault();
    const targetId = this.href.slice(1);
    const target = this.document.getElementById(targetId);
    if (target) {
      const barHeight = this.barRef?.nativeElement.offsetHeight ?? 0;
      const rectTop = target.getBoundingClientRect().top;
      const top = rectTop + window.pageYOffset - barHeight - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  private isDismissed(): boolean {
    return localStorage.getItem(DISMISS_KEY_PREFIX + this.persistKey) === 'true';
  }

  private observeBarHeight() {
    const el = this.barRef?.nativeElement;
    if (!el) {
      return;
    }
    const apply = () => {
      const h = el.offsetHeight;
      this.renderer.setStyle(this.document.documentElement, CSS_VAR_BAR_HEIGHT, `${h}px`, 2 /* RendererStyleFlags2.DashCase */);
    };
    apply();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => apply());
      this.resizeObserver.observe(el);
    }
  }

  private disconnectObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  private clearBarHeight() {
    this.renderer.removeStyle(this.document.documentElement, CSS_VAR_BAR_HEIGHT, 2);
  }
}
